import DevicesIcon from "@mui/icons-material/Devices";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { Box, Paper, Typography } from "@mui/material";

import cardBg from "../../assets/images/card-bg-land.png";
import Watermark from "../Watermark";

const PROJECT_TYPES = [
  {
    id: "website",
    title: "Website",
    description: "Business, portfolio, CMS or marketing website",
    icon: LanguageIcon,
  },
  {
    id: "mobileApp",
    title: "Mobile App",
    description: "iOS / Android app with core features",
    icon: PhoneIphoneIcon,
  },
  {
    id: "webAndApp",
    title: "Website + Mobile App",
    description: "Complete web & app solution",
    icon: DevicesIcon,
  },
  {
    id: "customSoftware",
    title: "Custom Software",
    description: "Platforms, dashboards, internal tools",
    icon: SettingsSuggestIcon,
  },
];

const CARD_HEIGHT = 180;

const Step1Type = ({ value, onChange }) => {
  return (
    <Box position="relative" mt={{ xs: 4, md: 6 }}>
      {/* ===== Heading ===== */}
      <Box
        textAlign="center"
        mb={{ xs: 4, md: 5 }}
        sx={{
          maxWidth: 720,
          mx: "auto",
        }}
      >
        {/* Eyebrow */}
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--color-midnight-60)",
            mb: 0.8,
          }}
        >
          Project setup
        </Typography>

        {/* Main heading */}
        <Typography
          component="h3"
          sx={{
            fontSize: {
              xs: "1.45rem",
              sm: "1.6rem",
              md: "1.85rem",
            },
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: "-0.015em",
            color: "var(--color-midnight)",
          }}
        >
          What are you{" "}
          <Box component="span" sx={{ color: "var(--color-fiery)" }}>
            building
          </Box>
          ?
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            mt: 1.2,
            fontSize: {
              xs: "0.9rem",
              sm: "0.95rem",
              md: "1.05rem",
            },
            lineHeight: 1.65,
            color: "var(--color-midnight-70)",
          }}
        >
          Choose the project type that best represents your product vision and
          technical scope.
        </Typography>
      </Box>

      {/* ===== FLEX CARDS ===== */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 2, md: 3 },
          justifyContent: "center",
        }}
      >
        {PROJECT_TYPES.map((item) => {
          const isSelected = value === item.id;
          const Icon = item.icon;

          return (
            <Paper
              key={item.id}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              onClick={() => onChange(item.id)}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && onChange(item.id)
              }
              sx={{
                position: "relative",
                overflow: "hidden",

                flex: {
                  xs: "0 0 100%",
                  sm: "0 0 48%",
                  md: "0 0 23%",
                },

                height: CARD_HEIGHT,
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",

                border: "1px solid",
                borderColor: isSelected
                  ? "var(--color-fiery)"
                  : "var(--color-midnight-15)",

                backgroundColor: "#fff",

                boxShadow: isSelected
                  ? "0 18px 44px rgba(233,79,55,0.25)"
                  : "0 10px 28px rgba(0,0,0,0.08)",

                transition: "all 0.3s ease",

                "&:hover": {
                  transform: { md: "translateY(-4px)" },
                  boxShadow: "0 22px 50px rgba(0,0,0,0.14)",
                },
              }}
            >
              {/* Background image */}
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${cardBg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  opacity: 1,
                  zIndex: 0,
                }}
              />

              {/* Gradient overlay */}
              {/* <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: isSelected
                    ? "linear-gradient(135deg, rgba(233,79,55,0.18), rgba(255,255,255,0.7))"
                    : "linear-gradient(135deg, rgba(233,79,55,0.08), rgba(255,255,255,0.9))",
                  zIndex: 1,
                }}
              /> */}
              <Box sx={{ position: "relative", zIndex: 2 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    mb: 2,
                    borderRadius: "14px",

                    backgroundColor: isSelected
                      ? "rgba(233,79,55,0.2)"
                      : "rgba(233,79,55,0.2)",

                    color: "var(--color-fiery)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",

                    /* smooth transitions */
                    transition:
                      "background-color 0.25s ease, transform 0.25s ease",

                    /* 🟡 IDLE animation (ALL cards) */
                    animation: isSelected
                      ? "iconBreatheStrong 2.2s ease-in-out infinite"
                      : "iconBreatheStrong 2.2s ease-in-out infinite",

                    /* 🔥 Extra pulse ONLY when selected */
                    // ...(isSelected && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: -8,
                      borderRadius: "20px",
                      border: "1px solid rgba(233,79,55,0.35)",
                      animation: "iconPulseStrong 2.2s ease-out infinite",
                    },
                    // }),

                    /* Accessibility */
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                      "&::after": {
                        animation: "none",
                      },
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 26 }} />
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    color: "var(--color-midnight)",
                    mb: 0.6,
                  }}
                >
                  {item.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: "var(--color-midnight-70)",
                    lineHeight: 1.45,
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* ===== Watermark ===== */}
      <Watermark />
    </Box>
  );
};

export default Step1Type;
